import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { withLastLocation } from 'react-router-last-location';
import ImageGallery from 'react-image-gallery';
import Likes from '../global/Likes';
import Loader from '../global/Loader';
import PageReady from '../global/PageReady';
import NotFoundPage from '../global/NotFoundPage';
import { getSightInfoById } from '../../selectors/sights';
import { fetchSightDetails } from '../../firestore/sights';
import { formatDistance, addressJSX, formatLink, descriptionToHTML, googleMapsURL, cfirst, formatMetaDescription } from '../../utils/helpers';
import { fromCityCentre, youCanAddLikesViaApplication, seeOnMap, address as addressTranslation, website, distanceFromCityCentre, sightCTABtnText } from '../../data/translations';
import pages from '../../data/pages';
import "react-image-gallery/styles/css/image-gallery.css";
import { sendGAEvent } from '../../utils/ga';
import GoogleAd from '../ads/GoogleAd';

class SightItemPage extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			notFoundDelayOver: false,
			showCTA: localStorage.getItem('sightpage-cta-seen') !== 'true'
		};
	};

	componentWillUnmount() {
		this.unsubscribeDetails && this.unsubscribeDetails();
		this.notFoundTimeout && clearTimeout(this.notFoundTimeout);
		if (this.state.showCTA) this.setCTASeen();
	};

	setDelayed404 = () => {
		const notFoundTimeout = setTimeout(() => {
			this.setState({ notFoundDelayOver: true })
		}, 10000);
		this.notFoundTimeout = notFoundTimeout;
	};

	fetchDetails = (id, detailsRef) => {
		this.unsubscribeDetails = fetchSightDetails(id, detailsRef, this.props.dispatch);
	};

	createMeta = ({ title, city, description }) => (
		<Helmet>
			<title>{title} - Opaste {cfirst(city)}</title>
			{description && <meta name="description" content={formatMetaDescription(description)} />}
		</Helmet>
	);

	onImageError = () => {
		this.setState(() => ({ imageError: true }));
	};

	setCTASeen = () => {
		localStorage.setItem('sightpage-cta-seen', 'true');
	};

	render() {
		const imagePath = '/images/sights/';
		const additionalImagesPath = '/images/sights/';
		const { id, detailsRef, details, title, image, distanceFromCentre, likes, coordinates, lang, dateString, city, googlePlaceId, lastLocation } = this.props;
		if (!details && detailsRef) this.fetchDetails(id, detailsRef);

		if (!id) {
			if (!this.notFoundTimeout) this.setDelayed404();
			return this.state.notFoundDelayOver ? <NotFoundPage /> : <Loader />
		}
		return (
			<Fragment>
				{this.createMeta({ title, city, description: details && details.description })}
				<div className="container">
					<div className="sight">
						{image && !this.state.imageError ? (
							<img className="sight__image" src={imagePath + image} alt={title} onError={this.onImageError} />
						) : (
							<div className="sight__image no-image" style={{ backgroundImage: `url('${imagePath}no-image.png')` }}>
							</div>
						)}
						<div className="sight__content">
							<div className="sight__info">
								<h1 className="sight__title">{title}</h1>
								{dateString && (
									<div className="sight__icon-and-text">
										<svg className="sight__icon city-icon" style={{ marginLeft: '2px' }} width="22" height="22" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M20 20h-4v-4h4v4zm-6-10h-4v4h4v-4zm6 0h-4v4h4v-4zm-12 6h-4v4h4v-4zm6 0h-4v4h4v-4zm-6-6h-4v4h4v-4zm16-8v22h-24v-22h3v1c0 1.103.897 2 2 2s2-.897 2-2v-1h10v1c0 1.103.897 2 2 2s2-.897 2-2v-1h3zm-2 6h-20v14h20v-14zm-2-7c0-.552-.447-1-1-1s-1 .448-1 1v2c0 .552.447 1 1 1s1-.448 1-1v-2zm-14 2c0 .552-.447 1-1 1s-1-.448-1-1v-2c0-.552.447-1 1-1s1 .448 1 1v2z"/></svg>
										<span className="sight__text">{dateString}</span>
									</div>
								)}
								{distanceFromCentre > 0 && (
									<div className="sight__icon-and-text">
										<svg aria-label={distanceFromCityCentre[lang]} className="sight__icon city-icon" width="24" height="24" xmlns="http://www.w3.org/2000/svg" fillRule="evenodd" clipRule="evenodd"><path d="M6 7v-7h13v10h5v14h-23v-17h5zm0 16v-4h-1v4h1zm8-4h-3v4h3v-4zm6 0h-1v4h1v-4zm2-7h-3v6h2v4h1v-10zm-5-10h-9v20h1v-5h7v5h1v-20zm-13 20v-4h2v-9h-3v13h1zm17-6h-1v-2h1v2zm-17-2h1v2h-1v-2zm8 1h-2v-2h2v2zm3 0h-2v-2h2v2zm-10-4v2h-1v-2h1zm7 1h-2v-2h2v2zm3 0h-2v-2h2v2zm-3-3h-2v-2h2v2zm3 0h-2v-2h2v2zm-3-3h-2v-2h2v2zm3 0h-2v-2h2v2z"/></svg>
										<span className="sight__text">{formatDistance(distanceFromCentre)} {fromCityCentre[lang]}</span>
									</div>
								)}
								{details && ((details.location && details.location.address) || coordinates) && (
									<div className="sight__icon-and-text sight__icon-and-text--location">
										<svg aria-label={addressTranslation[lang]} className="sight__icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M12 0c-4.198 0-8 3.403-8 7.602 0 4.198 3.469 9.21 8 16.398 4.531-7.188 8-12.2 8-16.398 0-4.199-3.801-7.602-8-7.602zm0 11c-1.657 0-3-1.343-3-3s1.343-3 3-3 3 1.343 3 3-1.343 3-3 3z"/></svg>
										{
											coordinates && details.location && details.location.address ? (
												<a
													className="sight__text"
													href={googleMapsURL(coordinates, googlePlaceId)}
													target="_blank"
													rel="noopener noreferrer"
													onClick={() => sendGAEvent('sightAddressClick', { title })}
												>
													{addressJSX(details.location.address, details.location.postal, details.location.city)}
												</a>
											) : !coordinates ? (
												<span className="sight__text">{addressJSX(details.location.address, details.location.postal, details.location.city)}</span>
											) : (
												<a
													className="sight__text"
													href={googleMapsURL(coordinates, googlePlaceId)}
													target="_blank"
													rel="noopener noreferrer"
													onClick={() => sendGAEvent('sightAddressClick', { title })}
												>
													{seeOnMap[lang]}
												</a>
											)
										}
									</div>
								)}
								{details && details.link && (
									<div className="sight__icon-and-text sight__icon-and-text--link">
										<svg aria-label={website[lang]} className="sight__icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M13.144 8.171c-.035-.066.342-.102.409-.102.074.009-.196.452-.409.102zm-2.152-3.072l.108-.031c.064.055-.072.095-.051.136.086.155.021.248.008.332-.014.085-.104.048-.149.093-.053.066.258.075.262.085.011.033-.375.089-.304.171.096.136.824-.195.708-.176.225-.113.029-.125-.097-.19-.043-.215-.079-.547-.213-.68l.088-.102c-.206-.299-.36.362-.36.362zm13.008 6.901c0 6.627-5.373 12-12 12-6.628 0-12-5.373-12-12s5.372-12 12-12c6.627 0 12 5.373 12 12zm-8.31-5.371c-.006-.146-.19-.284-.382-.031-.135.174-.111.439-.184.557-.104.175.567.339.567.174.025-.277.732-.063.87-.025.248.069.643-.226.211-.381-.355-.13-.542-.269-.574-.523 0 0 .188-.176.106-.166-.218.027-.614.786-.614.395zm6.296 5.371c0-1.035-.177-2.08-.357-2.632-.058-.174-.189-.312-.359-.378-.256-.1-1.337.597-1.5.254-.107-.229-.324.146-.572.008-.12-.066-.454-.515-.605-.46-.309.111.474.964.688 1.076.201-.152.852-.465.992-.038.268.804-.737 1.685-1.251 2.149-.768.694-.624-.449-1.147-.852-.275-.211-.272-.66-.55-.815-.124-.07-.693-.725-.688-.813l-.017.166c-.094.071-.294-.268-.315-.321 0 .295.48.765.639 1.001.271.405.416.995.748 1.326.178.178.858.914 1.035.898.193-.017.803-.458.911-.433.644.152-1.516 3.205-1.721 3.583-.169.317.138 1.101.113 1.476-.029.433-.37.573-.693.809-.346.253-.265.745-.556.925-.517.318-.889 1.353-1.623 1.348-.216-.001-1.14.36-1.261.007-.094-.256-.22-.45-.353-.703-.13-.248-.015-.505-.173-.724-.109-.152-.475-.497-.508-.677-.002-.155.117-.626.28-.708.229-.117.044-.458.016-.656-.048-.354-.267-.646-.53-.851-.389-.299-.188-.537-.097-.964 0-.204-.124-.472-.398-.392-.564.164-.393-.44-.804-.413-.296.021-.538.209-.813.292-.346.104-.7-.082-1.042-.125-1.407-.178-1.866-1.786-1.499-2.946.037-.19-.114-.542-.048-.689.158-.352.48-.747.762-1.014.158-.15.361-.112.547-.229.287-.181.291-.553.572-.781.4-.325.946-.318 1.468-.388.278-.037 1.336-.266 1.503-.06 0 .038.191.604-.019.572.433.023 1.05.749 1.461.579.211-.088.134-.736.567-.423.262.188 1.436.272 1.68.069.15-.124.234-.93.052-1.021.116.115-.611.124-.679.098-.12-.044-.232.114-.425.025.116.055-.646-.354-.218-.667-.179.131-.346-.037-.539.107-.133.108.062.18-.128.274-.302.153-.53-.525-.644-.602-.116-.076-1.014-.706-.77-.295l.789.785c-.039.025-.207-.286-.207-.059.053-.135.02.579-.104.347-.055-.089.09-.139.006-.268 0-.085-.228-.168-.272-.226-.125-.155-.457-.497-.637-.579-.05-.023-.764.087-.824.11-.07.098-.13.201-.179.311-.148.055-.287.126-.419.214l-.157.353c-.068.061-.765.291-.769.3.029-.075-.487-.171-.453-.321.038-.165.213-.68.168-.868-.048-.197 1.074.284 1.146-.235.029-.225.046-.487-.313-.525.068.008.695-.246.799-.36.146-.168.481-.442.724-.442.284 0 .223-.413.354-.615.131.053-.07.376.087.507-.01-.103.445.057.489.033.104-.054.684-.022.594-.294-.1-.277.051-.195.181-.253-.022.009.34-.619.402-.413-.043-.212-.421.074-.553.063-.305-.024-.176-.52-.061-.665.089-.115-.243-.256-.247-.036-.006.329-.312.627-.241 1.064.108.659-.735-.159-.809-.114-.28.17-.509-.214-.364-.444.148-.235.505-.224.652-.476.104-.178.225-.385.385-.52.535-.449.683-.09 1.216-.041.521.048.176.124.104.324-.069.19.286.258.409.099.07-.092.229-.323.298-.494.089-.222.901-.197.334-.536-.374-.223-2.004-.672-3.096-.672-.236 0-.401.263-.581.412-.356.295-1.268.874-1.775.698-.519-.179-1.63.66-1.808.666-.065.004.004-.634.358-.681-.153.023 1.247-.707 1.209-.859-.046-.18-2.799.822-2.676 1.023.059.092.299.092-.016.294-.18.109-.372.801-.541.801-.505.221-.537-.435-1.099.409l-.894.36c-1.328 1.411-2.247 3.198-2.58 5.183-.013.079.334.226.379.28.112.134.112.712.167.901.138.478.479.744.74 1.179.154.259.41.914.329 1.186.108-.178 1.07.815 1.246 1.022.414.487.733 1.077.061 1.559-.217.156.33 1.129.048 1.368l-.361.093c-.356.219-.195.756.021.982 1.818 1.901 4.38 3.087 7.22 3.087 5.517 0 9.989-4.472 9.989-9.989zm-11.507-6.357c.125-.055.293-.053.311-.22.015-.148.044-.046.08-.1.035-.053-.067-.138-.11-.146-.064-.014-.108.069-.149.104l-.072.019-.068.087.008.048-.087.106c-.085.084.002.139.087.102z"/></svg>
										<a
											className="sight__text"
											href={details.link}
											target="_blank"
											rel="noopener noreferrer"
											onClick={() => sendGAEvent('sightWebsiteClick', { title })}
										>
											{formatLink(details.link, lang)}
										</a>
									</div>
								)}
							</div>
							<Likes
								classes='sight__likes'
								likes={likes}
								tooltip={youCanAddLikesViaApplication[lang]}
								tooltipOnlyClickable
								tooltipTimeout={3000}
								lang={lang}
								gaEvent={'sightLike'}
								gaAction={{ title }}
							/>
						</div>
					</div>
					{ !details && <Loader showOfflineMessage={!navigator.onLine} lang={lang} />}
					{details && details.description && (
						<div className="sight__description">
							{descriptionToHTML(details.description)}
							{details.descriptionSource && <i className="sight__description-source">- {details.descriptionSource}</i>}
						</div>
					)}
					{(details && details.additionalImages && details.additionalImages.length > 0 && navigator.onLine) && (
						<ImageGallery
							showThumbnails={false}
							showPlayButton={false}
							useBrowserFullscreen={false}
							infinite={true}
							items={details.additionalImages.map(img => ({ original: additionalImagesPath + img, originalAlt: title }))}
							additionalClass="sight__gallery"
							defaultImage={`${additionalImagesPath}no-additional-image.png`}
						/>
					)}
					{ details && lastLocation === null && this.state.showCTA && (
						<div className="sight__cta">
							<Link
								className="sight__cta-btn"
								to={pages.sights.link[lang]}
								onClick={() => sendGAEvent('sightCTAClick', { title })}
							>
								<svg className="sight__cta-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M0 3.795l2.995-2.98 11.132 11.185-11.132 11.186-2.995-2.981 8.167-8.205-8.167-8.205zm18.04 8.205l-8.167 8.205 2.995 2.98 11.132-11.185-11.132-11.186-2.995 2.98 8.167 8.206z"/></svg>
								<span>{sightCTABtnText[lang]}</span>
							</Link>
						</div>
					)}

					{details && <GoogleAd style={{ marginTop: '5rem' }} slot="sightPage" />}

					{ details && <PageReady />}
				</div>
			</Fragment>
		);
	}
};

const mapStateToProps = (state, location) => ({
	lang: state.app.currentLanguage,
	city: state.app.currentLocation.city,
	...getSightInfoById(state.sights, state.sightDetails, location.match.params.id)
});

export default withLastLocation(connect(mapStateToProps)(SightItemPage));